import { useCallback, useSyncExternalStore } from 'react';

import type {
	BackgroundConversationActionsDescriptor,
	BackgroundConversationActionsReview,
} from '@atlassian/rovo-content-bridge-api-commands/commands/background-conversation-actions-command';

import type { PostedReplyMatch } from './hasPostedCommentAgentReply.ts';

export type BackgroundConversationActionsState =
	| { status: 'idle'; descriptor?: undefined }
	| { status: 'loading'; descriptor?: undefined }
	| { status: 'ready'; descriptor: BackgroundConversationActionsDescriptor }
	| { status: 'resolved'; descriptor?: undefined }
	| { status: 'unavailable'; descriptor?: undefined; review?: BackgroundConversationActionsReview }
	| {
			status: 'resolving';
			descriptor: BackgroundConversationActionsDescriptor;
			resolution: 'confirmed' | 'cancelled';
	  };

type Submission = {
	invocationId: string;
	state: BackgroundConversationActionsState;
	replyMatch?: PostedReplyMatch;
};

// Retain tab-local submission state across comment popup remounts, without treating it as success.
const submitted = new Map<string, Submission>();
const listeners = new Set<() => void>();
const subscribe = (listener: () => void) => {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
};

export const getSubmittedCommentAgentAction = (conversationId: string): Submission | undefined =>
	submitted.get(conversationId);

export function setSubmittedCommentAgentAction(
	conversationId: string,
	invocationId?: string,
	state: BackgroundConversationActionsState = { status: 'resolved' },
	replyMatch?: PostedReplyMatch,
): void {
	submitted.delete(conversationId);
	if (invocationId) submitted.set(conversationId, { invocationId, state, replyMatch });
	if (submitted.size > 100) submitted.delete(submitted.keys().next().value as string);
	listeners.forEach((listener) => listener());
}

export function useSubmittedCommentAgentAction(conversationId: string): Submission | undefined {
	const subscribeToConversation = useCallback(
		(listener: () => void) => (conversationId ? subscribe(listener) : () => {}),
		[conversationId],
	);
	return useSyncExternalStore(
		subscribeToConversation,
		() => submitted.get(conversationId),
		() => undefined,
	);
}
